import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { translations, supportedLanguages, defaultLanguage, getTranslation } from '../locales';
import { settingsAPI } from '../utils/api';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);
  const [isLoading, setIsLoading] = useState(false);
  const [adminLanguage, setAdminLanguage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));

  const getStoredUserRole = () => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.role || null;
    } catch (error) {
      return null;
    }
  };

  // Get current translations
  const currentTranslations = translations[currentLanguage] || translations[defaultLanguage];

  // Translation function with fallback and interpolation support
  const t = (key, variables) => {
    // Handle the case where second parameter is fallback text (legacy usage)
    let fallback = key;
    let interpolationVars = {};
    
    if (typeof variables === 'string') {
      // Legacy usage: t(key, fallbackString)
      fallback = variables;
    } else if (typeof variables === 'object' && variables !== null) {
      // New usage: t(key, {count: 5, name: "John"})
      interpolationVars = variables;
    }
    
    const translation = getTranslation(currentTranslations, key, fallback);
    
    // Debug log for offers, cvs, and notes page translations
    if (key.startsWith('offers.') || key.startsWith('cvs.') || key.startsWith('notes.')) {
      // Debug: Translation key and variables
    }
    
    // Handle interpolation if variables are provided
    if (Object.keys(interpolationVars).length > 0) {
      let result = translation;
      
      // Replace all {variableName} placeholders
      Object.keys(interpolationVars).forEach(varName => {
        const placeholder = `{${varName}}`;
        const value = interpolationVars[varName];
        result = result.replace(new RegExp(placeholder, 'g'), value);
      });
      
      return result;
    }
    
    return translation;
  };

  // This context is now ONLY for private pages (authenticated users)
  // Public pages use PublicLanguageContext instead

  // Change language function for private pages only
  const changeLanguage = async (languageCode) => {
    if (!supportedLanguages.some(lang => lang.code === languageCode)) {
      // Language not supported
      return;
    }

    setIsLoading(true);
    
    try {
      const role = getStoredUserRole();

      if (role === 'super_admin') {
        setCurrentLanguage(languageCode);
        localStorage.setItem('preferredLanguage', languageCode);
        return;
      }

      // For private pages, save to user preferences in database
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await settingsAPI.updatePreferences({ language: languageCode });
          setCurrentLanguage(languageCode);
          localStorage.setItem('preferredLanguage', languageCode);
        } catch (error) {
          // Failed to save language preference
        }
      }
    } catch (error) {
      // Error changing language
    } finally {
      setIsLoading(false);
    }
  };

  // Load user's preferred language from server when authenticated
  const loadUserLanguagePreference = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const role = getStoredUserRole();
      if (role === 'super_admin') {
        const savedLanguage = localStorage.getItem('preferredLanguage');
        const nextLanguage = supportedLanguages.some((lang) => lang.code === savedLanguage)
          ? savedLanguage
          : defaultLanguage;
        setAdminLanguage(nextLanguage);
        setCurrentLanguage(nextLanguage);
        return;
      }

      const response = await settingsAPI.getPreferences();
      const preferences = response.data;
      const dbLanguage = preferences.language || defaultLanguage;
      const adminLang = preferences.adminLanguage || dbLanguage;
        
        // Loaded user language preferences
        
        setAdminLanguage(adminLang);
        
        // Always update current language (this context is private-only)
        setCurrentLanguage(dbLanguage);
        localStorage.setItem('preferredLanguage', dbLanguage);
    } catch (error) {
      // Failed to load user language preference
      setAdminLanguage(defaultLanguage);
    }
  };

  // Initialize language on mount for authenticated users
  useEffect(() => {
    const token = localStorage.getItem('token');
    const newAuthState = !!token;
    
    setIsAuthenticated(newAuthState);
    
    if (newAuthState) {
      // Load user's language preference from database
      loadUserLanguagePreference();
    }
  }, [location.pathname]);

  // Monitor authentication state changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    const newAuthState = !!token;
    
    if (newAuthState !== isAuthenticated) {
      setIsAuthenticated(newAuthState);
      
      if (newAuthState) {
        // User just logged in - load their DB preference
        loadUserLanguagePreference();
      } else {
        // User logged out - reset to default
        setAdminLanguage(null);
        setCurrentLanguage(defaultLanguage);
      }
    }
  }, [isAuthenticated]);

  // Listen for token changes (login/logout)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'token') {
        const newToken = event.newValue;
        const newAuthState = !!newToken;
        setIsAuthenticated(newAuthState);
        
        if (newAuthState) {
          loadUserLanguagePreference();
        } else {
          setAdminLanguage(null);
          setCurrentLanguage(defaultLanguage);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated]);

  // Helper function to get admin language for workspace features
  const getAdminLanguage = () => {
    return adminLanguage || defaultLanguage;
  };

  const value = {
    currentLanguage,
    supportedLanguages,
    t,
    changeLanguage,
    isLoading,
    loadUserLanguagePreference,
    getAdminLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
